import { Descriptions, Flex, Tag, Typography } from 'antd';
import type { WmsShipmentInboundType } from 'schemas/shipment/inbound/detail';
import { COLOR } from '@styles/theme';
import { DateUtility, DateFormat } from '@utility/date';
import {
  getShipmentStatusMeta,
  type InboundDetailDisplay,
} from '@utility/utils/shipment-display';
import { CommonUtility } from '@utility/common';

const { Text, Title } = Typography;

type InboundShipmentInfoTabProps = {
  data: WmsShipmentInboundType;
  display: InboundDetailDisplay;
};

function formatDateTime(iso?: string | null) {
  if (!iso) return '—';
  return DateUtility.formatDate(iso, DateFormat.dateTime);
}

export function InboundShipmentInfoTab({ data, display }: InboundShipmentInfoTabProps) {
  const statusMeta = getShipmentStatusMeta(data.wmsStatus || data.status);

  return (
    <Flex vertical gap={16} style={{ padding: '8px 0 0' }}>
      <Descriptions
        column={1}
        colon={false}
        labelStyle={{ width: 120, color: COLOR.textSecondary, fontSize: 14 }}
        contentStyle={{ fontSize: 14, fontWeight: 600 }}
        items={[
          {
            key: 'status',
            label: 'Status',
            children: (
              <Tag color={statusMeta.color} style={{ margin: 0 }}>
                {statusMeta.label}
              </Tag>
            ),
          },
          {
            key: 'type',
            label: 'Workflow',
            children:
              CommonUtility.removeUnderscoreAndCapitalize(data.workflowType) || '—',
          },
          { key: 'origin', label: 'Origin', children: display.supplierName },
          { key: 'destination', label: 'Destination', children: display.warehouseName },
          {
            key: 'method',
            label: 'Method',
            children:
              CommonUtility.removeUnderscoreAndCapitalize(data.shippingType) || '—',
          },
        ]}
      />

      <Flex vertical gap={4}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Shipped: {formatDateTime(data.shipped)}
        </Text>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Estimated Arrival: {formatDateTime(data.ETA)}
        </Text>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Arrived: {formatDateTime(data.arrived || data.workflow?.arrivedAt)}
        </Text>
      </Flex>

      <Flex vertical gap={8}>
        <Title level={5} style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
          Progress
        </Title>
        <Text style={{ fontSize: 14 }}>
          {data.progress?.percentage ?? 0}% — {data.progress?.totalReceived ?? 0} of{' '}
          {data.progress?.totalExpected ?? 0} received
        </Text>
      </Flex>
    </Flex>
  );
}
